import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { api } from "./api";

/** Same mapping as WasteGenerated.jsx / AddData.js */
export const WASTE_TYPES_BY_CATEGORY = {
  "Hazardous Waste": [
    "Asbestos",
    "Mineral oil",
    "Electrical item",
    "ETP Sludge",
    "Effluent",
  ],
  "Non-Hazardous Waste": [
    "Metals",
    "Tyres",
    "Wood",
    "Books",
    "Glass",
    "Clothing",
    "Organic mixed food and garden waste",
    "Plastic",
    "Paper and board",
    "Sewage",
    "STP Sludge",
  ],
  "Construction & Demolition Waste": [
    "Aggregates",
    "Average construction",
    "Asphalt",
    "Bricks",
    "Concrete",
    "Insulation",
    "Soils",
    "Plasterboard",
  ],
};

export const WASTE_MANAGEMENT_METHODS_BY_WASTE_TYPE = {
  Aggregates: ["Recycling", "Landfilling"],
  "Average construction": ["Recycling", "Incineration"],
  Asbestos: ["Landfilling"],
  Asphalt: ["Recycling", "Landfilling"],
  Bricks: ["Recycling", "Landfilling"],
  Concrete: ["Recycling", "Landfilling"],
  Insulation: ["Recycling", "Landfilling"],
  Metals: ["Recycling", "Landfilling"],
  Soils: ["Recycling", "Landfilling"],
  "Mineral oil": ["Recycling", "Incineration"],
  Plasterboard: ["Recycling", "Landfilling"],
  Tyres: ["Recycling"],
  Wood: ["Recycling", "Incineration", "Landfilling"],
  Books: ["Recycling", "Incineration", "Landfilling"],
  Glass: ["Recycling", "Incineration", "Landfilling"],
  Clothing: ["Recycling", "Incineration", "Landfilling"],
  "Organic mixed food and garden waste": [
    "Incineration",
    "Landfilling",
    "Composting",
  ],
  "Electrical item": ["Recycling", "Incineration", "Landfilling"],
  Plastic: ["Recycling", "Incineration", "Landfilling"],
  "Paper and board": ["Recycling", "Incineration", "Landfilling"],
  Sludge: ["Recycling", "Landfilling", "Incineration"],
  Effluent: ["Recycling"],
  "ETP Sludge": ["Recycling", "Incineration", "Landfilling"],
  "STP Sludge": ["Landfilling", "Incineration", "Composting"],
  Sewage: ["Recycling"],
};

const WASTE_CATEGORY_OPTIONS = Object.keys(WASTE_TYPES_BY_CATEGORY);

export const HEADER_KEYS = [
  "Month (in 01/25 or Jan-25)",
  "Waste Category",
  "Waste Type",
  "Waste Management Method",
  "Vendor Name",
  "Quantity of Waste Generated (Tonnes, or Cubic Metres for Wastewater)",
  "Density(in Case of Waste Type is Effluent,Sewage)",
  "Quantity of Waste Managed",
];

const MONTH_HEADER = HEADER_KEYS[0];
const DATA_START_ROW = 2;
const DATA_END_ROW = 100;
const TEMPLATE_ROW_COUNT = 20;

const COL = {
  month: "A",
  category: "B",
  wasteType: "C",
  method: "D",
  vendor: "E",
};

const colLetter = (colIndex1Based) => {
  let n = colIndex1Based;
  let s = "";
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_MIN_DATE = new Date(2000, 0, 1);
const MONTH_MAX_DATE = new Date(2035, 11, 31);

const toFirstOfMonth = (year, monthIndex0) => new Date(year, monthIndex0, 1);

const isMonthInRange = (date) =>
  date instanceof Date &&
  !Number.isNaN(date.getTime()) &&
  date >= MONTH_MIN_DATE &&
  date <= MONTH_MAX_DATE;

const excelSerialToDate = (serial) => {
  const serialNumber = Number(serial);
  if (!Number.isFinite(serialNumber) || serialNumber <= 0) return null;
  const excelEpoch = Date.UTC(1899, 11, 30);
  const date = new Date(excelEpoch + serialNumber * 24 * 60 * 60 * 1000);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Parse bulk-upload month values into the 1st of a valid month/year. */
export const parseMonthInput = (value) => {
  if (value === undefined || value === null || value === "") return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const date = toFirstOfMonth(value.getFullYear(), value.getMonth());
    return isMonthInRange(date) ? date : null;
  }

  if (typeof value === "number") {
    const date = excelSerialToDate(value);
    if (!date) return null;
    const normalized = toFirstOfMonth(date.getFullYear(), date.getMonth());
    return isMonthInRange(normalized) ? normalized : null;
  }

  const text = String(value).trim();
  if (!text) return null;

  const slashShort = text.match(/^(\d{1,2})\/(\d{2})$/u);
  if (slashShort) {
    const month = Number(slashShort[1]);
    const year = 2000 + Number(slashShort[2]);
    if (month >= 1 && month <= 12) {
      const date = toFirstOfMonth(year, month - 1);
      return isMonthInRange(date) ? date : null;
    }
  }

  const slashLong = text.match(/^(\d{1,2})\/(\d{4})$/u);
  if (slashLong) {
    const month = Number(slashLong[1]);
    const year = Number(slashLong[2]);
    if (month >= 1 && month <= 12) {
      const date = toFirstOfMonth(year, month - 1);
      return isMonthInRange(date) ? date : null;
    }
  }

  const abbrMatch = text.match(/^([A-Za-z]{3})-(\d{2,4})$/u);
  if (abbrMatch) {
    const monthIndex = MONTH_ABBR.findIndex(
      (name) => name.toLowerCase() === abbrMatch[1].toLowerCase(),
    );
    if (monthIndex >= 0) {
      let year = Number(abbrMatch[2]);
      if (year < 100) year += 2000;
      const date = toFirstOfMonth(year, monthIndex);
      return isMonthInRange(date) ? date : null;
    }
  }

  if (/^\d+(\.\d+)?$/u.test(text)) {
    const date = excelSerialToDate(text);
    if (!date) return null;
    const normalized = toFirstOfMonth(date.getFullYear(), date.getMonth());
    return isMonthInRange(normalized) ? normalized : null;
  }

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    const normalized = toFirstOfMonth(parsed.getFullYear(), parsed.getMonth());
    return isMonthInRange(normalized) ? normalized : null;
  }

  return null;
};

const normalizeWasteGeneratedPrefillValue = (header, value) => {
  if (header !== MONTH_HEADER) {
    return value ?? "";
  }

  if (value === undefined || value === null || value === "") {
    return "";
  }

  return parseMonthInput(value) || "";
};

const normalizeWasteManagementTypes = (value) => {
  if (Array.isArray(value)) {
    return value
      .map(String)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }
  return [];
};

const styleHeaderRow = (worksheet, colCount) => {
  worksheet.getRow(1).height = 32;
  for (let c = 1; c <= colCount; c += 1) {
    const cell = worksheet.getCell(1, c);
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4F81BD" },
    };
    cell.numFmt = undefined;
  }
};

/**
 * Month column uses Excel date format so entries like 01/01/2026 display as 01/26.
 * Date validation rejects values such as 757575 that are not valid month dates.
 */
const applyMonthDateFormatAndValidation = (worksheet, colIndex = 1) => {
  for (let row = DATA_START_ROW; row <= DATA_END_ROW; row += 1) {
    const cell = worksheet.getCell(row, colIndex);

    if (cell.value === null || cell.value === undefined) {
      cell.value = "";
    } else if (cell.value !== "") {
      const parsed = parseMonthInput(cell.value);
      cell.value = parsed || "";
    }

    cell.numFmt = "mm/yy";
    cell.dataValidation = {
      type: "date",
      operator: "between",
      allowBlank: true,
      formulae: [MONTH_MIN_DATE, MONTH_MAX_DATE],
      showErrorMessage: true,
      errorTitle: "Invalid month",
      error: "Enter month as 01/25, Jan-25, or a valid date (MM/YY).",
      showInputMessage: true,
      promptTitle: "Month (01/25 or Jan-25)",
      prompt: "Type 01/25, Jan-25, or any date — Excel will show it as MM/YY.",
    };
  }
};

const setColumnWidths = (worksheet, headers) => {
  headers.forEach((h, i) => {
    const col = worksheet.getColumn(i + 1);
    col.width = Math.min(48, Math.max(18, String(h).length + 4));
  });
};

const buildEmptyTemplateRow = () =>
  Object.fromEntries(HEADER_KEYS.map((key) => [key, ""]));

const writeListColumn = (
  masterSheet,
  workbook,
  startCol,
  rangeName,
  values,
) => {
  masterSheet.getCell(1, startCol).value = rangeName;
  values.forEach((value, index) => {
    masterSheet.getCell(index + 2, startCol).value = value;
  });

  const columnLetter = masterSheet.getColumn(startCol).letter;
  const lastRow = values.length + 1;
  const rangeRef = `MasterData!$${columnLetter}$2:$${columnLetter}$${lastRow}`;
  workbook.definedNames.add(rangeName, rangeRef);

  return { rangeRef, nextCol: startCol + 1, columnLetter };
};

const writeHorizontalSponge = (masterSheet, startCol, itemsByHeader) => {
  const headers = Object.keys(itemsByHeader);
  headers.forEach((header, idx) => {
    const col = startCol + idx;
    masterSheet.getCell(1, col).value = header;
    itemsByHeader[header].forEach((item, rowIdx) => {
      masterSheet.getCell(rowIdx + 2, col).value = item;
    });
  });

  const startLetter = masterSheet.getColumn(startCol).letter;
  const endLetter = masterSheet.getColumn(startCol + headers.length - 1).letter;

  return {
    headerRange: `MasterData!$${startLetter}$1:$${endLetter}$1`,
    dataAnchor: `MasterData!$${startLetter}$2`,
    nextCol: startCol + headers.length,
    startLetter,
  };
};

const applyDropdown = (worksheet, columnRange, rangeRef) => {
  worksheet.dataValidations.add(columnRange, {
    type: "list",
    allowBlank: true,
    formulae: [`=${rangeRef}`],
    showErrorMessage: true,
    errorTitle: "Invalid Value",
    error: "Please choose a value from the dropdown.",
  });
};

const applyDynamicHorizontalDropdown = (
  worksheet,
  targetColumn,
  parentColumn,
  dataAnchor,
  headerRange,
  blankCell,
  rowStart = DATA_START_ROW,
  rowEnd = DATA_END_ROW,
) => {
  for (let row = rowStart; row <= rowEnd; row += 1) {
    worksheet.getCell(`${targetColumn}${row}`).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [
        `=IF($${parentColumn}${row}="",${blankCell},OFFSET(${dataAnchor},0,MATCH($${parentColumn}${row},${headerRange},0)-1,COUNTA(OFFSET(${dataAnchor},0,MATCH($${parentColumn}${row},${headerRange},0)-1,100,1)),1))`,
      ],
      showErrorMessage: true,
      errorTitle: "Invalid Value",
      error: "Select the parent field first, then choose from the list.",
    };
  }
};

const applyDynamicVerticalDropdown = (
  worksheet,
  targetColumn,
  parentColumn,
  valueColumn,
  lookupColumn,
  lookupStartRow,
  lookupEndRow,
  blankCell,
  rowStart = DATA_START_ROW,
  rowEnd = DATA_END_ROW,
) => {
  const lookupRange = `MasterData!$${lookupColumn}$${lookupStartRow}:$${lookupColumn}$${lookupEndRow}`;
  const valueAnchor = `MasterData!$${valueColumn}$1`;

  for (let row = rowStart; row <= rowEnd; row += 1) {
    worksheet.getCell(`${targetColumn}${row}`).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [
        `=IF($${parentColumn}${row}="",${blankCell},OFFSET(${valueAnchor},MATCH($${parentColumn}${row},${lookupRange},0)-1,0,COUNTIF(${lookupRange},$${parentColumn}${row}),1))`,
      ],
      showErrorMessage: true,
      errorTitle: "Invalid Value",
      error: "Select waste management method first, then choose vendor.",
    };
  }
};

const buildVendorsByMethod = (vendors = []) => {
  const vendorsByMethod = {};
  vendors.forEach((vendor) => {
    const vendorName = vendor?.vendorLegalName;
    normalizeWasteManagementTypes(vendor?.wasteManagementType).forEach(
      (method) => {
        if (!vendorsByMethod[method]) vendorsByMethod[method] = [];
        if (vendorName && !vendorsByMethod[method].includes(vendorName)) {
          vendorsByMethod[method].push(vendorName);
        }
      },
    );
  });

  const allMethods = [
    ...new Set([
      ...Object.values(WASTE_MANAGEMENT_METHODS_BY_WASTE_TYPE).flat(),
      ...Object.keys(vendorsByMethod),
    ]),
  ];

  allMethods.forEach((method) => {
    if (!vendorsByMethod[method]) vendorsByMethod[method] = [];
    if (!vendorsByMethod[method].includes("Other")) {
      vendorsByMethod[method].push("Other");
    }
  });

  return vendorsByMethod;
};

/**
 * Plain .xlsx template with dependent dropdowns:
 * Category → Waste Type → Method → Vendor (no macros).
 */
export async function downloadWasteGeneratedTemplateExcel({
  get,
  fileName = "Waste Generated",
  rows = [],
} = {}) {
  if (typeof get !== "function") {
    throw new Error(
      "downloadWasteGeneratedTemplateExcel requires `get` from api utils",
    );
  }

  let vendors = [];
  try {
    const vendorRes = await get(`${api}/master/wasteManagement`);
    vendors = Array.isArray(vendorRes?.data?.data) ? vendorRes.data.data : [];
  } catch {
    vendors = [];
  }

  const vendorsByMethod = buildVendorsByMethod(vendors);
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("data");

  const columns = HEADER_KEYS.map((header) => ({ header, key: header }));
  worksheet.columns = columns;

  const prefillRows = Array.isArray(rows) ? rows : [];
  const emptyRowCount = Math.max(TEMPLATE_ROW_COUNT, prefillRows.length || 0);

  for (let i = 0; i < emptyRowCount; i += 1) {
    const rowData = prefillRows[i] || buildEmptyTemplateRow();
    const rowValues = {};
    HEADER_KEYS.forEach((header) => {
      rowValues[header] = normalizeWasteGeneratedPrefillValue(
        header,
        rowData?.[header],
      );
    });
    worksheet.addRow(rowValues);
  }

  setColumnWidths(worksheet, HEADER_KEYS);
  applyMonthDateFormatAndValidation(worksheet, 1);
  styleHeaderRow(worksheet, HEADER_KEYS.length);

  const masterSheet = workbook.addWorksheet("MasterData");
  let currentColumn = 1;

  const categoryList = writeListColumn(
    masterSheet,
    workbook,
    currentColumn,
    "CategoryList",
    WASTE_CATEGORY_OPTIONS,
  );
  currentColumn = categoryList.nextCol;

  const typeSponge = writeHorizontalSponge(
    masterSheet,
    currentColumn,
    WASTE_TYPES_BY_CATEGORY,
  );
  currentColumn = typeSponge.nextCol;

  const methodSponge = writeHorizontalSponge(
    masterSheet,
    currentColumn,
    WASTE_MANAGEMENT_METHODS_BY_WASTE_TYPE,
  );
  currentColumn = methodSponge.nextCol;

  const methodCol = currentColumn;
  const vendorCol = currentColumn + 1;
  masterSheet.getCell(1, methodCol).value = "Method";
  masterSheet.getCell(1, vendorCol).value = "Other";

  let methodVendorRow = 2;
  Object.entries(vendorsByMethod).forEach(([method, names]) => {
    names.forEach((vendorName) => {
      masterSheet.getCell(methodVendorRow, methodCol).value = method;
      masterSheet.getCell(methodVendorRow, vendorCol).value = vendorName;
      methodVendorRow += 1;
    });
  });

  const methodVendorEndRow = Math.max(methodVendorRow - 1, 2);
  const blankRow =
    Math.max(
      WASTE_CATEGORY_OPTIONS.length,
      ...Object.values(WASTE_TYPES_BY_CATEGORY).map((items) => items.length),
      ...Object.values(WASTE_MANAGEMENT_METHODS_BY_WASTE_TYPE).map(
        (items) => items.length,
      ),
      methodVendorEndRow,
    ) + 4;
  const blankCell = `MasterData!$${typeSponge.startLetter}$${blankRow}`;

  masterSheet.state = "veryHidden";

  applyDropdown(
    worksheet,
    `${COL.category}${DATA_START_ROW}:${COL.category}${DATA_END_ROW}`,
    categoryList.rangeRef,
  );

  applyDynamicHorizontalDropdown(
    worksheet,
    COL.wasteType,
    COL.category,
    typeSponge.dataAnchor,
    typeSponge.headerRange,
    blankCell,
  );

  applyDynamicHorizontalDropdown(
    worksheet,
    COL.method,
    COL.wasteType,
    methodSponge.dataAnchor,
    methodSponge.headerRange,
    blankCell,
  );

  applyDynamicVerticalDropdown(
    worksheet,
    COL.vendor,
    COL.method,
    colLetter(vendorCol),
    colLetter(methodCol),
    2,
    methodVendorEndRow,
    blankCell,
  );

  // Re-apply header styling after validations (ExcelJS can reset row-1 styles).
  styleHeaderRow(worksheet, HEADER_KEYS.length);
  applyMonthDateFormatAndValidation(worksheet, 1);

  const buffer = await workbook.xlsx.writeBuffer();
  const safeName = String(fileName || "Waste Generated").replace(
    /\.(xlsx|xlsm)$/i,
    "",
  );
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${safeName}.xlsx`,
  );
}
